// Every conversation on the account, in one panel.
//
// A thread is just a distinct conversation_id on the messages — there is no conversations table — so
// the Worker derives the library from the messages themselves. That is what lets this browser see the
// conversations that were only ever spoken to the phone.
//
// One component, two presentations, because the same list wants opposite treatment on each screen:
// docked beside the transcript on a laptop, where there is room to read both and picking a thread is
// part of how you work; an overlay drawer on a phone, where the transcript needs the whole width. The
// switch is entirely in CSS — see `.ochat-side` — so there is no duplicated markup to drift.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Conversation, ConversationKind } from '../api/chat';
import { relativeTime } from './home-support';
import { Spinner } from '../components/ui';
import { IconPen, IconSearch } from '../components/Icons';

/**
 * A reflection needs no label — it is the default and saying so on every row is just noise. The other
 * two do, because a two-line voice memo sitting between long reflections is otherwise unexplained.
 */
const KIND_LABEL: Record<ConversationKind, string> = {
  reflection: '',
  voice: 'Spoken',
  entry: 'From an entry',
};

export function ChatLibrary({
  open,
  conversations,
  currentId,
  loading,
  onClose,
  onOpen,
  onNew,
}: {
  open: boolean;
  conversations: Conversation[];
  currentId: string;
  loading: boolean;
  onClose(): void;
  onOpen(id: string): void;
  onNew(): void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');

  // Escape closes, and focus moves into the panel so a keyboard user is not left behind on the page
  // underneath. Only while it behaves as a modal drawer: stealing focus into a docked sidebar every
  // time it appears would pull the caret out of the message box mid-sentence.
  useEffect(() => {
    if (!open) return;
    const isDrawer = window.matchMedia('(max-width: 1023px)').matches;
    if (isDrawer) panelRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && window.matchMedia('(max-width: 1023px)').matches) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const found = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return conversations;
    return conversations.filter((c) => c.title.toLowerCase().includes(needle));
  }, [conversations, query]);

  if (!open) return null;

  return (
    <>
      <div className="othreads-scrim" onClick={onClose} aria-hidden />
      <aside
        className="ochat-side"
        ref={panelRef}
        tabIndex={-1}
        aria-label="Your conversations"
      >
        {/* No close button of its own. The header's toggle is the only one, and on a phone it floats
            into this row — see `.othreads-open.is-on` in the drawer media query. The min-height is
            what reserves the space for it, since nothing in this row is that tall on its own. */}
        <div className="othreads-head">
          <span className="olabel">Conversations</span>
        </div>

        <button className="othreads-new" onClick={onNew}>
          <IconPen />
          New conversation
        </button>

        {/* Only worth the space once there is enough here to lose something in. */}
        {conversations.length > 6 ? (
          <div className="othreads-search">
            <IconSearch />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search conversations"
              aria-label="Search conversations"
            />
          </div>
        ) : null}

        <div className="othreads-list">
          {loading ? (
            <Spinner label="Finding your conversations…" />
          ) : conversations.length === 0 ? (
            <p className="othreads-empty">
              Nothing here yet. Anything you say — here or on your phone — will show up in this list.
            </p>
          ) : found.length === 0 ? (
            <p className="othreads-empty">Nothing matches “{query.trim()}”.</p>
          ) : (
            found.map((thread) => (
              <button
                key={thread.id}
                className={`othread-item${thread.id === currentId ? ' is-current' : ''}`}
                onClick={() => onOpen(thread.id)}
                aria-current={thread.id === currentId}
              >
                <span className="othread-item-title">{thread.title}</span>
                <span className="othread-item-meta">
                  {KIND_LABEL[thread.kind] ? `${KIND_LABEL[thread.kind]} · ` : ''}
                  {relativeTime(thread.lastMessageAt)}
                  {' · '}
                  {thread.messageCount} {thread.messageCount === 1 ? 'message' : 'messages'}
                </span>
              </button>
            ))
          )}
        </div>
      </aside>
    </>
  );
}
